/**
 * Inventory Types Service - React Hooks
 *
 * Client-side hooks for managing inventory types with React Query.
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { REFERENCE_DATA_CONFIG } from '../query-config';
import { directApiClient } from '@/lib/api/direct-client';
import { getInventoryTypesCacheKey } from './utils';
import type { InventoryItemType } from './types';

/**
 * Hook to fetch all inventory item types
 * Uses directApiClient for direct API calls (bypasses proxy)
 */
export function useInventoryItemTypes() {
  const query = useQuery({
    queryKey: getInventoryTypesCacheKey(),
    queryFn: async () => {
      // Direct API call via directApiClient (bypasses proxy)
      const types = await directApiClient.get<InventoryItemType[]>(
        '/inventory-item-types'
      );
      return { types, total: types.length };
    },
    ...REFERENCE_DATA_CONFIG
  });

  return {
    types: query.data?.types || [],
    total: query.data?.total || 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch
  };
}
